/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { IMultiAxisDatasetVisitor } from '../../../../store/types/MultiAxisDatasetTypes';
import { IAxisItem } from '../../../../store/types/StateTypes';
import { formatLabel } from './GenericSeriesUtil';
import i18n from '../../../../../i18n/AdHocBundle.properties';

/* eslint-disable no-param-reassign */

const MAX_CATEGORY_LABEL_LENGTH = 150;

export interface IFlatCategoriesVisitorContext {
    categories: Array<any>,
    stack: string[],
    selectedRowIndex: number,
    levels: IAxisItem[],
    measures: Array<any>,
    inMeasure?: boolean,
    labelGenerator?: Function,
    idToLabelMap?: Map<string, string>,
    includeMeasureName: boolean
}

function concatLabel(labels: Array<string>) {
    return labels.join(', ');
}

function prepareCategoryName(categoryName: string) {
    if (categoryName === null) {
        return '';
    }

    if (categoryName === 'other_node') {
        categoryName = i18n['adhoc.node.other.node'];
    }

    return categoryName
        .substring(0, MAX_CATEGORY_LABEL_LENGTH)
        .replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/&lt;br&gt;/g, '<br>');
}

function prepareCategoryNameForFlatVisitor(categoryName: string) {
    let newCategoryName = prepareCategoryName(categoryName);
    if (newCategoryName === '') {
        // we should return blank space because otherwise it leads to issue JS-65651
        newCategoryName = ' ';
    }
    return newCategoryName;
}

const flatCategoriesVisitor: IMultiAxisDatasetVisitor<IFlatCategoriesVisitorContext> = {
    preVisit(ctxt) {
        const context = ctxt;
        context.inMeasure = false;
        return context;
    },

    preVisitNode(node, ctxt, currentDepth) {
        const context = ctxt;
        const labelGenerator = ctxt.labelGenerator || concatLabel;

        let { label } :any = node;

        if (node.isMeasure) {
            if (context.includeMeasureName) {
                // in case label contains id this conversion may help to get actual label
                if (context.idToLabelMap && context.idToLabelMap.get(label)) {
                    label = context.idToLabelMap.get(label);
                }
                const useAggregationFormat = context.selectedRowIndex <= (currentDepth - 1);
                label = formatLabel(label, context.levels[currentDepth - 1], useAggregationFormat);
                context.stack.push(label);
            }
        } else {
            const useAggregationFormat = context.selectedRowIndex <= (currentDepth - 1);
            label = formatLabel(label, context.levels[currentDepth - 1], useAggregationFormat);
            context.stack.push(label);
        }

        if (!context.inMeasure && node.isMeasure) {
            context.inMeasure = true;
        }

        if (currentDepth === context.selectedRowIndex && !node.isTotal) {
            const labels = context.stack.slice(1);
            if (context.inMeasure || !context.measures.length) {
                label = labelGenerator(labels);
                if (context.selectedRowIndex === 0 && label === '') {
                    label = i18n['adhoc.node.total.node'];
                }
                const categoryName = prepareCategoryNameForFlatVisitor(label);
                context.categories.push({
                    name: categoryName
                });
            } else {
                context.measures.forEach((measure: any) => {
                    label = labelGenerator(labels.concat([measure.label]));
                    if (context.selectedRowIndex === 0 && label === '') {
                        label = i18n['adhoc.node.total.node'];
                    }
                    const categoryName = prepareCategoryNameForFlatVisitor(label);
                    context.categories.push({
                        name: categoryName
                    });
                });
            }
        }

        return context;
    },

    postVisitNode(node, context) {
        context.stack.pop();

        if (context.inMeasure && node.isMeasure) {
            context.inMeasure = false;
        }

        return context;
    }
};

export interface ITreeCategoryNode {
    name: string,
    categories: Array<ITreeCategoryNode | string>
}

export interface ITreeCategoriesVisitorContext {
    categories?: Array<ITreeCategoryNode | string>
    current?: ITreeCategoryNode
    stack: Array<ITreeCategoryNode>,
    selectedRowIndex: number,
    measureLabels: Array<string>,
    levels: IAxisItem[],
    idToLabelMap?: Map<string, string>,
    inMeasures?: boolean,
    includeMeasureName?: boolean
}

const treeCategoriesVisitor: IMultiAxisDatasetVisitor<ITreeCategoriesVisitorContext> = {
    preVisit(ctxt) {
        const context = ctxt;
        context.current = {
            name: '',
            categories: []
        };

        context.inMeasures = false;

        return context;

    },
    preVisitNode(node, ctxt, currentDepth, currentIndex) {
        const context = ctxt;
        if (context.current && currentDepth <= context.selectedRowIndex && !node.isTotal) {

            let { label } = node;
            if (node.isMeasure) {
                label = context.measureLabels[currentIndex];
            }
            // in case label contains id this conversion may help to get actual label
            if (context.idToLabelMap && context.idToLabelMap.get(label)) {
                label = <string>context.idToLabelMap.get(label);
            }
            const useAggregationFormat = context.selectedRowIndex <= (currentDepth - 1);
            label = formatLabel(label, ctxt.levels[currentDepth - 1], useAggregationFormat);
            if (context.selectedRowIndex === 0 && label === '') {
                label = i18n['adhoc.node.total.node'];
            }
            const categoryName = prepareCategoryName(label);
            const category: ITreeCategoryNode = {
                name: categoryName,
                categories: []
            };
            context.current.categories.push(category);
            context.stack.push(context.current);
            context.current = category;

            if (node.isMeasure) {
                context.inMeasures = true;
            }
        }

        return context;
    },

    postVisitNode(node, ctxt, currentDepth) {
        const context = ctxt;
        if (!node.isTotal) {
            const { current } = context;
            if (current && current.categories.length) {
                const firstChild = current.categories[0];
                if (typeof firstChild !== 'string' && !firstChild.categories.length) {
                    current.categories = current.categories.map((category: any) => (category.name));
                }
            }

            if (currentDepth <= context.selectedRowIndex) {
                if (context.selectedRowIndex === currentDepth && !context.inMeasures && current) {
                    if (ctxt.includeMeasureName) {
                        current.categories = context.measureLabels;
                    }
                }

                context.current = context.stack.pop();
            }
        }

        return context;
    },

    postVisit(ctxt) {
        const context = ctxt;
        if (context.current) {
            const underRoot = context.current.categories[0] as ITreeCategoryNode;
            context.categories = (underRoot && underRoot.categories) ? underRoot.categories : context.current.categories;
        }
        if (ctxt.includeMeasureName === false) {
            // we need to remove measure name from categories, and to do it we might take into account that
            // measure is always located at the top level and have fields beneath.
            const firstCategory :any = context.categories ? context.categories[0] : {};
            if (firstCategory && firstCategory.categories) {
                context.categories = firstCategory.categories;
            }
        }
        return context;
    }
};

export { flatCategoriesVisitor, treeCategoriesVisitor };
